import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { getAverageMinxMax } from '@/api/ts/average'
import { convertToVPD } from '@/Util/MeasurementUtils'

export const useLiveData = ({
  sectionAId,
  sectionBId,
  metrics,
  currentTrial,
  type = 'envirosense',
  fromDate,
  toDate
}) => {
  const dispatch = useDispatch()
  const [dataRequested, setDataRequested] = useState(null)
  const [averageData, setAverageData] = useState(null)
  const [error, setError] = useState(null)

  if (!metrics || !Array.isArray(metrics)) {
    throw new Error('useLiveData requires a metrics array')
  }

  const hasRequiredData = sectionAId && sectionBId && currentTrial?.zone?.id

  const measurementIds = useMemo(() => {
    const baseMetrics = new Set()

    metrics.forEach(metric => {
      if (metric.id === 'vapour_pressure_deficit') {
        baseMetrics.add('temperature')
        baseMetrics.add('humidity')
      } else {
        baseMetrics.add(metric.id)
      }
    })

    return Array.from(baseMetrics)
  }, [metrics])

  const effectiveFromDate = useMemo(() => {
    if (fromDate) return fromDate
    const date = new Date()
    date.setHours(date.getHours() - 1)
    return date.toISOString()
  }, [fromDate])

  const effectiveToDate = useMemo(() => {
    if (toDate) return toDate
    return new Date().toISOString()
  }, [toDate])

  const requestIdentifier = useMemo(
    () =>
      `${sectionAId}-${sectionBId}-${type}-${measurementIds.join(
        ','
      )}-${effectiveFromDate}-${effectiveToDate}`,
    [
      sectionAId,
      sectionBId,
      type,
      measurementIds,
      effectiveFromDate,
      effectiveToDate
    ]
  )

  useEffect(() => {
    if (
      !hasRequiredData ||
      measurementIds.length === 0 ||
      requestIdentifier === dataRequested
    )
      return

    const fetchData = async () => {
      try {
        const fetchParams = {
          table: currentTrial.zone.id,
          measurements: measurementIds,
          fromDate: effectiveFromDate,
          toDate: effectiveToDate,
          sensorIds: [sectionAId, sectionBId]
        }

        const result = await getAverageMinxMax(fetchParams)
        if (result.error) {
          setError(result.error)
        } else {
          setAverageData(result.averageMinMax)
        }
      } catch (err) {
        setError(err.message)
      }
    }

    fetchData()
    setDataRequested(requestIdentifier)
  }, [
    dispatch,
    sectionAId,
    sectionBId,
    measurementIds,
    currentTrial,
    requestIdentifier,
    dataRequested,
    hasRequiredData,
    effectiveFromDate,
    effectiveToDate
  ])

  const processedData = useMemo(() => {
    if (!hasRequiredData || !averageData?.rows || !averageData.columns)
      return {}

    const { rows, columns } = averageData
    const indices = {
      sensorId: columns.indexOf('sensorId'),
      measurement: columns.indexOf('measure_name'),
      max: columns.indexOf('max'),
      min: columns.indexOf('min'),
      sum: columns.indexOf('sum'),
      count: columns.indexOf('count')
    }

    const rawData = rows.reduce((data, row) => {
      const sensorId = row[indices.sensorId]
      const section = sensorId === sectionAId ? 'A' : 'B'
      const measurementId = row[indices.measurement]
      const key = `section${section}_${measurementId}`

      // Calculate average from sum and count
      const sum = parseFloat(row[indices.sum])
      const count = parseFloat(row[indices.count])
      const average = count > 0 ? sum / count : 0

      data[key] = {
        average,
        max: parseFloat(row[indices.max]),
        min: parseFloat(row[indices.min])
      }
      return data
    }, {})

    const finalData = { ...rawData }
    const sections = ['sectionA', 'sectionB']
    const hasVPD = metrics.some(m => m.id === 'vapour_pressure_deficit')

    for (const section of sections) {
      if (hasVPD) {
        const tempKey = `${section}_temperature`
        const humidityKey = `${section}_humidity`

        if (rawData[tempKey] && rawData[humidityKey]) {
          const temperature = rawData[tempKey].average
          const humidity = rawData[humidityKey].average
          finalData[`${section}_vapour_pressure_deficit`] = {
            average: convertToVPD({ temperature, humidity })
          }
        }
      }
    }

    return finalData
  }, [averageData, sectionAId, sectionBId, hasRequiredData, metrics])

  if (!hasRequiredData) return { liveData: {}, isLoading: false }

  return {
    liveData: processedData,
    isLoading: !averageData && !error,
    error
  }
}
